import { useEffect, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import { WarningIcon } from '@phosphor-icons/react'

import { Button } from '@components/shared/Button'
import { PageWrapper } from '@components/shared/PageWrapper'
import { ReturnModal } from '@components/modals/ReturnModal'
import { emotionTheme } from '@styles/theme'
import { useLayout } from '@hooks/useLayout'
import { useUser } from '@hooks/useUser'

import { ConsolidatedEditAlert } from './components/ConsolidatedEditAlert'
import { ConsolidatedEntrySkeleton } from './components/ConsolidatedEntrySkeleton'
import { ConsolidatedGrid } from './components/ConsolidatedGrid'
import { ConsolidatedHeaderCard } from './components/ConsolidatedHeaderCard'
import { InsertValuesCard } from './components/InsertValuesCard'
import { useConsolidatedEntry } from './context'
import { formatCompetenceLabel } from './helpers/formatCompetenceLabel'
import { shouldShowConsolidatedEditAlert } from './helpers/shouldShowConsolidatedEditAlert'

import type { PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'

import * as S from './styles'

export const ConsolidatedEntry = () => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { sideBarData } = useLayout()
  const { consolidatedData, getPreviaSemanal, liberarPreviaSemanal, isLiberarPreviaSemanalLoading } =
    useConsolidatedEntry()

  const isRouteDataLoading = sideBarData.length === 0

  const [openReleaseModal, setOpenReleaseModal] = useState(false)

  useEffect(() => {
    if (filiData?.filiCode && filiData?.filiCode !== 'ALL' && filiData?.startPeriod) {
      getPreviaSemanal({ referenceDate: filiData.startPeriod, branchCode: filiData.filiCode })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filiData])

  const handleConfirmRelease = async () => {
    if (!filiData?.startPeriod || !filiData?.filiCode || filiData?.filiCode === 'ALL') return

    const success = await liberarPreviaSemanal({
      referenceDate: filiData.startPeriod,
      branchCode: filiData.filiCode
    })

    if (success) setOpenReleaseModal(false)
  }

  return (
    <PageWrapper
      breadcrumbData={[
        { label: 'Gestão Financeira' },
        { label: 'Prévia' },
        { label: 'Lançamento Consolidado' }
      ]}
    >
      {shouldShowConsolidatedEditAlert(consolidatedData?.permissions?.canSubmit) && <ConsolidatedEditAlert />}

      {isRouteDataLoading ? (
        <ConsolidatedEntrySkeleton />
      ) : (
        <>
          <ConsolidatedHeaderCard
            unitLabel={consolidatedData?.economicGroupName}
            competenceLabel={
              consolidatedData?.referenceDate ? formatCompetenceLabel(consolidatedData.referenceDate) : undefined
            }
            status={
              consolidatedData
                ? { code: consolidatedData.statusCurrentPreview as PreviewsStatusProps }
                : undefined
            }
          />

          <Box sx={{ marginTop: '24px' }}>
            <ConsolidatedGrid />
          </Box>

          <Box sx={{ marginTop: '24px' }}>
            <InsertValuesCard />
          </Box>

          <S.Buttons>
            <Button
              width={isMobile ? '100%' : 190}
              colorType="solidBlue"
              disabled={!consolidatedData?.permissions?.canSubmit}
              onClick={() => setOpenReleaseModal(true)}
            >
              Liberar Prévia
            </Button>
          </S.Buttons>

          <ReturnModal
            open={openReleaseModal}
            headerIcon={<WarningIcon size={24} weight="bold" />}
            headerTitle="Liberar Prévia"
            message="Tem certeza de que deseja liberar a Prévia desta competência?"
            cancelButtonText="Cancelar"
            confirmButtonText="Confirmar"
            onClose={setOpenReleaseModal}
            isLoading={isLiberarPreviaSemanalLoading}
            onConfirm={handleConfirmRelease}
          />
        </>
      )}
    </PageWrapper>
  )
}
